import React, { useMemo } from 'react';
import {
  AppAdminFormLabel,
  AppButton,
  AppCheckbox,
  AppMultiSelect,
  AppSelect,
} from '@/components/atoms';
import { Search, Download } from '@/components/atoms/AppIcon';
import { RA2NDatePicker } from '@/components/organisms';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2, SERVICE_TYPE_LABELS } from '@/constants/serviceType';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { TenantSiteTypeItem } from '@/services/tenants';

const dayFormat = parseFormat(JA_JP.format);

function ra2nDateToYYYYMMDD(value: string): string {
  if (!value?.trim()) return '';
  const d = parseDate(value, dayFormat);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

type OrderRezeptSheetsFilterPanelProps = {
  tenants: TenantSiteTypeItem[];
  showSiteFilter: boolean;
  siteTypes: string[];
  hasAllSelected: boolean;
  onChangeSiteTypes: (next: string[]) => void;
  serviceType: string;
  onChangeServiceType: (next: string) => void;
  orderDateFrom: string;
  onChangeOrderDateFrom: (next: string) => void;
  orderDateTo: string;
  onChangeOrderDateTo: (next: string) => void;
  incDelivered: boolean;
  onChangeIncDelivered: (next: boolean) => void;
  onSearch: () => void;
  onCsv: () => void;
  csvLoading?: boolean;
};

const OrderRezeptSheetsFilterPanel: React.FC<OrderRezeptSheetsFilterPanelProps> = ({
  tenants,
  showSiteFilter,
  siteTypes,
  hasAllSelected,
  onChangeSiteTypes,
  serviceType,
  onChangeServiceType,
  orderDateFrom,
  onChangeOrderDateFrom,
  orderDateTo,
  onChangeOrderDateTo,
  incDelivered,
  onChangeIncDelivered,
  onSearch,
  onCsv,
  csvLoading = false,
}) => {
  const { colors } = useTheme();

  const tenantSiteTypes = useMemo(
    () => tenants.map(t => t.site_type).filter(Boolean) as string[],
    [tenants]
  );

  const siteTypeOptions = [
    { label: 'すべて', value: 'all' },
    ...tenants.map(t => ({ label: t.name, value: t.site_type })),
  ];

  const serviceTypeOptions = [
    { label: 'すべて', value: '' },
    { label: SERVICE_TYPE_LABELS[SERVICE_TYPE_A2], value: SERVICE_TYPE_A2 },
    { label: SERVICE_TYPE_LABELS[SERVICE_TYPE_R2], value: SERVICE_TYPE_R2 },
  ];

  return (
    <div className="mb-4 rounded-md border bg-white p-4">
      {/* Row 1: all filter inputs on one line */}
      <div className="flex flex-wrap items-end gap-4">
        {showSiteFilter && (
          <div className="min-w-[180px] flex-1">
            <AppAdminFormLabel label="サイト区分" />
            <AppMultiSelect
              id="order_rezept_sheet_site_type"
              placeholder="サイト区分"
              value={siteTypes}
              className="w-full"
              maxTagCount={hasAllSelected ? 0 : 'responsive'}
              maxTagPlaceholder={hasAllSelected ? () => 'すべて' : undefined}
              showSearch
              onInputKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              options={siteTypeOptions}
              onChange={values => {
                const next = (values as string[]) || [];
                if (next.includes('all')) {
                  onChangeSiteTypes(tenantSiteTypes);
                  return;
                }
                onChangeSiteTypes(next.filter(v => v !== 'all'));
              }}
            />
          </div>
        )}
        <div className="min-w-[160px] flex-1">
          <AppAdminFormLabel label="サービス区分" />
          <AppSelect
            id="order_rezept_sheet_service_type"
            placeholder="すべて"
            value={serviceType || undefined}
            allowClear
            options={serviceTypeOptions}
            onChange={v => onChangeServiceType((v as string) ?? '')}
            className="w-full"
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <AppAdminFormLabel label="受注開始日" />
          <RA2NDatePicker
            picker="day"
            className="w-full"
            value={orderDateFrom || ''}
            onChange={v => onChangeOrderDateFrom(ra2nDateToYYYYMMDD(v))}
            placeholder="受注開始日"
            onEnter={onSearch}
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <AppAdminFormLabel label="受注終了日" />
          <RA2NDatePicker
            picker="day"
            className="w-full"
            value={orderDateTo || ''}
            onChange={v => onChangeOrderDateTo(ra2nDateToYYYYMMDD(v))}
            placeholder="受注終了日"
            onEnter={onSearch}
          />
        </div>
        <div className="flex items-center min-h-[40px] self-end">
          <AppCheckbox
            checked={incDelivered}
            onChange={e => onChangeIncDelivered(e.target.checked)}
          >
            出荷済み含む
          </AppCheckbox>
        </div>
      </div>
      {/* Row 2: buttons centered */}
      <div className="flex justify-center gap-2 mt-4">
        <AppButton
          onClick={onSearch}
          type="primary"
          icon={<Search />}
          style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
        >
          検索
        </AppButton>
        <AppButton
          onClick={onCsv}
          type="primary"
          icon={<Download />}
          style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
          className="btn-ra2-hover"
          loading={csvLoading}
        >
          CSV
        </AppButton>
      </div>
    </div>
  );
};

export default OrderRezeptSheetsFilterPanel;
